// Coupon / Activation Key Generator — Admin generates, users redeem
import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Copy, Ban, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';

const VALIDITY_PRESETS = [
  { label: '15 days', days: 15 },
  { label: '30 days', days: 30 },
  { label: '180 days', days: 180 },
  { label: '365 days', days: 365 },
  { label: 'Custom', days: 0 },
];

const TOOLS = [
  'marketplace', 'asm', 'vyapaar-master', 'trial-room', 'samadhan', 'avx',
  'smart-vyapar', 'citypro', 'algotrading', 'tsb', 'magic-qr', 'digital-narad',
  'supertool', 'brandmark', 'leadgen', 'anant-yojana', 'anant-tarot',
];

export default function AdminActivationKeys() {
  const [keys, setKeys] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, redeemed: 0 });
  const [filter, setFilter] = useState({ tool_slug: '', active: '' });
  const [form, setForm] = useState({ tool_slug: 'marketplace', validity_days: 30, count: 1, usage_limit: 1, prefix: 'AN', note: '', plan_tier: 'basic' });
  const STORE_TIERS = [['basic','Basic Store'],['standard','Standard Store'],['prime','Prime Gold'],['vip','VIP Showroom'],['basic_yearly','Basic · Yearly'],['standard_yearly','Standard · Yearly'],['prime_yearly','Prime Gold · Yearly'],['vip_yearly','VIP · Yearly']];
  const isMarketplaceKey = ['marketplace','vyapaar-master','smart-vyapar','anant-webshop'].includes(form.tool_slug);
  const [customDays, setCustomDays] = useState(30);
  const [preset, setPreset] = useState(30);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter.tool_slug) params.tool_slug = filter.tool_slug;
      if (filter.active) params.active = filter.active === 'active';
      const r = await api.get('/admin/activation-keys/list', { params });
      setKeys(r.data.keys || []);
      setStats(r.data.stats || { total: 0, active: 0, redeemed: 0 });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    const validity = preset === 0 ? customDays : preset;
    if (validity < 1) { toast.error('Validity must be >= 1 day'); return; }
    try {
      const r = await api.post('/admin/activation-keys/generate', { ...form, validity_days: validity, plan_tier: isMarketplaceKey ? form.plan_tier : null });
      toast.success(`✅ ${r.data.generated} key(s) generated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Generate failed'); }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('📋 Copied ' + code);
  };

  const revoke = async (code) => {
    if (!window.confirm(`Revoke key ${code}?`)) return;
    try {
      await api.post(`/admin/activation-keys/${code}/revoke`);
      toast.success('✅ Revoked'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const assign = async (code) => {
    const usernameOrId = window.prompt(`Assign key ${code} to which user? Enter user_id, email, or username:`, '');
    if (!usernameOrId) return;
    try {
      // Search user
      const s = await api.get('/admin/user-tools/search', { params: { q: usernameOrId, limit: 5 } });
      const users = s.data.users || [];
      if (users.length === 0) { toast.error('No user found'); return; }
      const u = users[0];
      if (!window.confirm(`Assign key ${code} to ${u.name} (@${u.username})?`)) return;
      await api.post(`/admin/activation-keys/${code}/assign`, { user_id: u.id });
      toast.success(`✅ Assigned to @${u.username}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assign failed'); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="admin-activation-keys">
        <div>
          <h1 className="text-2xl md:text-3xl font-black">🔑 Coupon / Activation Keys</h1>
          <p className="text-xs opacity-70">Generate keys for tool activation · User redeems to unlock full plan</p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="p-3 rounded-xl bg-slate-900/70 border border-slate-800 text-center">
            <div className="text-2xl font-black text-emerald-300">{stats.total}</div>
            <div className="text-[10px] opacity-60">Total Keys</div>
          </div>
          <div className="p-3 rounded-xl bg-slate-900/70 border border-slate-800 text-center">
            <div className="text-2xl font-black text-cyan-300">{stats.active}</div>
            <div className="text-[10px] opacity-60">Active</div>
          </div>
          <div className="p-3 rounded-xl bg-slate-900/70 border border-slate-800 text-center">
            <div className="text-2xl font-black text-yellow-300">{stats.redeemed}</div>
            <div className="text-[10px] opacity-60">Redeemed</div>
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-r from-indigo-900/60 to-purple-900/60 border border-indigo-400/40 p-4 space-y-2" data-testid="keys-generate-form">
          <div className="text-sm font-black flex items-center gap-1"><Sparkles size={14} /> Generate New Keys</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <label className="text-[10px] font-black opacity-80">Tool
              <select value={form.tool_slug} onChange={(e) => setForm({ ...form, tool_slug: e.target.value })} data-testid="keys-tool"
                      className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
                {TOOLS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            {isMarketplaceKey && (
              <label className="text-[10px] font-black opacity-80">Store Plan (pack)
                <select value={form.plan_tier} onChange={(e) => setForm({ ...form, plan_tier: e.target.value })} data-testid="keys-plan-tier"
                        className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
                  {STORE_TIERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            )}
            <label className="text-[10px] font-black opacity-80">Validity
              <select value={preset} onChange={(e) => setPreset(Number(e.target.value))} data-testid="keys-validity-preset"
                      className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
                {VALIDITY_PRESETS.map(v => <option key={v.days} value={v.days}>{v.label}</option>)}
              </select>
            </label>
            {preset === 0 && (
              <label className="text-[10px] font-black opacity-80">Custom Days
                <input type="number" min="1" max="3650" value={customDays} onChange={(e) => setCustomDays(Number(e.target.value))} data-testid="keys-custom-days"
                       className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
              </label>
            )}
            <label className="text-[10px] font-black opacity-80">Count
              <input type="number" min="1" max="200" value={form.count} onChange={(e) => setForm({ ...form, count: Number(e.target.value) })} data-testid="keys-count"
                     className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </label>
            <label className="text-[10px] font-black opacity-80">Usage Limit (per key)
              <input type="number" min="1" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: Number(e.target.value) })} data-testid="keys-usage-limit"
                     className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </label>
            <label className="text-[10px] font-black opacity-80">Prefix
              <input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} maxLength={6} data-testid="keys-prefix"
                     className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm uppercase" />
            </label>
            <label className="text-[10px] font-black opacity-80 col-span-2">Note (optional)
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="keys-note"
                     className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </label>
          </div>
          <button onClick={generate} data-testid="keys-generate-submit"
                  className="w-full py-2 rounded-lg bg-yellow-400 text-slate-900 font-black text-sm flex items-center justify-center gap-1">
            <KeyRound size={14} /> Generate {form.count} Key{form.count > 1 ? 's' : ''}
          </button>
        </div>

        <div className="flex gap-2 flex-wrap">
          <select value={filter.tool_slug} onChange={(e) => setFilter({ ...filter, tool_slug: e.target.value })} data-testid="keys-filter-tool"
                  className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">All tools</option>
            {TOOLS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filter.active} onChange={(e) => setFilter({ ...filter, active: e.target.value })} data-testid="keys-filter-active"
                  className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">All statuses</option>
            <option value="active">Active only</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>

        <div className="space-y-1.5">
          {keys.length === 0 && <div className="text-center py-8 opacity-60 text-xs">No keys yet</div>}
          {keys.map((k) => (
            <div key={k.code} className="rounded-lg bg-slate-900/70 border border-slate-800 p-2 flex items-center gap-2" data-testid={`keys-row-${k.code}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono font-black text-yellow-300 truncate">{k.code}</div>
                <div className="text-[10px] opacity-70 truncate">
                  {k.tool_slug}{k.plan_tier ? ` [${k.plan_tier}]` : ''} · {k.validity_days}d · Used {k.used_count}/{k.usage_limit}
                  {!k.active && <span className="text-rose-400 ml-1">· REVOKED</span>}
                  {k.assigned_to_username && <span className="text-cyan-300 ml-1">· 🎯 @{k.assigned_to_username}</span>}
                  {k.note && <span className="opacity-60 ml-1">· {k.note}</span>}
                </div>
              </div>
              <button onClick={() => copy(k.code)} data-testid={`keys-copy-${k.code}`}
                      className="px-2 py-1 rounded bg-slate-700 text-[10px] font-black flex items-center gap-1">
                <Copy size={10} /> Copy
              </button>
              {k.active && !k.assigned_to_user_id && (
                <button onClick={() => assign(k.code)} data-testid={`keys-assign-${k.code}`}
                        className="px-2 py-1 rounded bg-cyan-500 text-slate-900 text-[10px] font-black">
                  🎯 Assign
                </button>
              )}
              {k.active && (
                <button onClick={() => revoke(k.code)} data-testid={`keys-revoke-${k.code}`}
                        className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black flex items-center gap-1">
                  <Ban size={10} /> Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
